import { pixelsToMm } from '@/utils/unitsUtils';
import { v4 as uuidv4 } from 'uuid';
import fontManagerService from '@/utils/fontManagerService';
import { useColor } from '@/composables/color';

(function () {
    'ngInject';

    angular.module('app.shared.services').factory('TextService', textService);

    function textService(MultipleFontService, ElementsColorService) {
        const { getColorObj } = useColor();
        const objectsWithTextMasks = {};

        function _updateTextElements(text, brotherTexts, canvas) {
            // Update each brother text but don't do it in a loop because it will result in very poor
            //  performance on products with many text elements
            var update = function (start, count, objects, doRender) {
                if (doRender) {
                    for (var n = start; n < start + count && n < objects.length; ++n) {
                        objects[n].set('text', text);
                    }
                    canvas.requestRenderAll();
                    start += count;
                }

                if (start < objects.length) {
                    // Skip every 1 animation frame to decrease CPU load
                    doRender = !doRender;
                    requestAnimationFrame(() => update(start, count, objects, doRender));
                }
            };

            requestAnimationFrame(() => update(0, 5, brotherTexts, false));
        }

        const debouncedUpdateTextElements = _.debounce(_updateTextElements, 500);

        function updateTexts(text, object, canvas) {
            // Update the modified text element
            object.set('text', text);
            canvas.renderAll();

            // Find objects with the same id
            const brotherTexts = canvas.getObjects().filter(function (o) {
                if (o.get('id') === object.id && o !== object) {
                    return o;
                }
            });

            // If we have too many texts, debounce the function, we don't want to update the text elements so quickly
            if (brotherTexts.length > 40) {
                debouncedUpdateTextElements(text, brotherTexts, canvas);
            } else {
                _updateTextElements(text, brotherTexts, canvas);
            }
        }

        function buildBoundedTextProperties(parameters, override = {}) {
            const defaultFont = fontManagerService.defaultFont;
            const { id, canvasWidth, canvasHeight, itext, zIndex, pageEpsId, strokeDashArray, name } = parameters;
            const fontsMap = new Map();
            fontsMap.set(1, defaultFont.Path);
            const shadowColorObj = getColorObj({ hex: '#7258E0', a: 1 });
            const defaultShadowColor = MultipleFontService.createColorObj({
                hex: shadowColorObj.hex,
                cmyk: `${shadowColorObj.cmyk.c},${shadowColorObj.cmyk.m},${shadowColorObj.cmyk.y},${shadowColorObj.cmyk.k}`, 
            });
            const defaultColor = MultipleFontService.createColorObj({
                hex: '#000000',
                cmyk: '0,0,0,1'
            });
            const fontColorsMap = new Map([[1, defaultColor]]);
            const outlineColorsMap = new Map([[1, defaultColor]]);
            const strokeColorsMap = new Map([[1, defaultColor]]);
            const shadowColorsMap = new Map([[1, defaultShadowColor]]);
            const left = canvasWidth / 2;
            const top = canvasHeight / 2;
            const width = 0.3 * canvasWidth;
            const height = 0.3 * canvasHeight;

            return {
                outlineWidth: 0,
                textAlign: 'center',
                fontColorOption: 1,
                outlineColorOption: 1,
                tracking: 0,
                lineSpacing: 1,
                skewX: 0,
                skewY: 0,
                angle: 0,
                rotation: 0,
                locked: false,
                flipX: false,
                flipY: false,
                text: '',
                width,
                height,
                originX: 'center',
                originY: 'center',
                left,
                top,
                strokeDashArray,
                centeredRotation: true,
                lockScalingFlip: true,

                minSizePx: fabric.util.parseUnit(15 + 'pt'),
                maxSizePx: fabric.util.parseUnit(50 + 'pt'),
                multiline: false,
                verticalText: false,
                caps: false,
                fontPath: defaultFont.Path,
                fontName: defaultFont.Name + '_' + defaultFont.Style,
                fontFamily: defaultFont.Name + '_' + defaultFont.Style,
                fontOption: 1,

                color: fontColorsMap.get(1),
                outlineColor: outlineColorsMap.get(1),
                outlineColorsMap,
                id,
                uuid: uuidv4(),
                zIndex,
                fontsMap,
                fontColorsMap,
                strokeColor: strokeColorsMap.get(1),
                strokeWidth: 0,
                strokeColorsMap,
                strokeColorOption: 1,
                Itext: itext,
                defaultValue: '',
                prefix: '',
                suffix: '',
                ligatures: true,
                referenceId: null,
                isOverlay: false,
                isPrintArea: false,
                pageEpsId,
                visible: true,
                name: name || '',
                allowEdit: false,
                useAsMask: false,
                autofillSource: '',
                dateFormat: {},
                shadowType: null,
                shadowOffset: 0,
                shadowAngle: 0,
                shadowBlur: 0,
                shadowColor: shadowColorsMap.get(1),
                shadowColorsMap,
                shadowColorOption: 1,
                ...override
            };
        }

        function generateBoundedText(parameters, override = {}) {
            const defaultText = buildBoundedTextProperties(parameters, override);

            const { flipX, flipY } = parameters;
            const text = new (parameters.isTextPath ? fabric.TextTransform : fabric.BoundedText)(defaultText);

            text.setFlip(flipX || false, flipY || false);
            return text;
        }

        function buildCircularTextProperties(parameters, override = {}) {
            const defaultFont = fontManagerService.defaultFont;
            let fontsMap = new Map();
            // set default option to the fonts map
            fontsMap.set(1, defaultFont.Path);
            const defaultColor = MultipleFontService.createColorObj({
                hex: '#000000',
                cmyk: '0,0,0,1'
            });
            const fontColorsMap = new Map([[1, defaultColor]]);
            const outlineColorsMap = new Map([[1, defaultColor]]);
            const { canvasWidth, canvasHeight, id, zIndex, pathWidth, pageEpsId } = parameters;
            return {
                pathWidth,
                width: 0.2 * canvasWidth,
                height: 0.2 * canvasWidth,
                originX: 'center',
                originY: 'center',
                left: canvasWidth / 2,
                top: canvasHeight / 2,
                centeredRotation: true,
                lockSkewingX: true,
                lockSkewingY: true,
                lockScalingFlip: true,
                verticalDiameter: 0.3 * canvasWidth,
                horizontalDiameter: 0.3 * canvasWidth,

                fill: '#000000',
                color: fontColorsMap.get(1),

                stroke: '#000000',
                outlineColor: '#000000',
                strokeWidth: 0,
                outlineColorsMap,
                outlineColorOption: 1,

                fontPath: defaultFont.Path,
                fontName: defaultFont.Name + '_' + defaultFont.Style,
                fontFamily: defaultFont.Name + '_' + defaultFont.Style,
                fontSize: fabric.util.parseUnit(10 + 'pt'),
                maxSizePx: fabric.util.parseUnit(10 + 'pt'),
                minSizePx: fabric.util.parseUnit(0 + 'pt'),
                fontOption: 1,
                id,
                uuid: uuidv4(),
                // add fonts map to circular text object
                fontsMap: fontsMap,
                // add font color map to circular text object
                fontColorsMap: fontColorsMap,
                zIndex,
                defaultValue: '',
                prefix: '',
                suffix: '',
                referenceId: null,
                isOverlay: false,
                isPrintArea: false,
                pageEpsId,
                ...override
            };
        }

        function generatePathIText(parameters, override = {}) {
            const { flipX, flipY } = parameters;
            const circularTextParams = buildCircularTextProperties(parameters, override);

            const text = new fabric.PathIText('A', circularTextParams);
            text.setFlip(flipX || false, flipY || false);
            return text;
        }

        function loadTextData($scope) {
            if (!$scope.textSelected) {
                return;
            }
            // Load data
            $scope.selectedTextProperties.text = $scope.textSelected.getText();
            $scope.selectedTextProperties.centerX = pixelsToMm($scope.textSelected.left, $scope.DPI);
            $scope.selectedTextProperties.centerY = pixelsToMm($scope.textSelected.top, $scope.DPI);
            $scope.selectedTextProperties.rotation = $scope.textSelected.angle;

            const w = $scope.textSelected.getWidth();
            const h = $scope.textSelected.getHeight();
            $scope.selectedTextProperties.width = pixelsToMm(w, $scope.DPI);
            $scope.selectedTextProperties.height = pixelsToMm(h, $scope.DPI);

            $scope.selectedTextProperties.minSizePx = fabric.util.pxToPt($scope.textSelected.minSizePx);
            $scope.selectedTextProperties.maxSizePx = fabric.util.pxToPt($scope.textSelected.maxSizePx);
            $scope.selectedTextProperties.multiline = $scope.textSelected.multiline;
            $scope.selectedTextProperties.verticalText = $scope.textSelected.verticalText;
            $scope.selectedTextProperties.caps = $scope.textSelected.caps;
            $scope.selectedTextProperties.fontPath = $scope.textSelected.fontPath;

            if ($scope.hasOwnProperty('searchFont')) {
                const selectedFont = fontManagerService.findFontByPath($scope.selectedTextProperties.fontPath);
                if (selectedFont) {
                    const fontName = `${selectedFont.Name} (${selectedFont.Style})`;
                    $scope.searchFont = {
                        selectedText: fontName,
                        previousSelectedText: fontName,
                        previousSelectedValue: $scope.selectedTextProperties.fontPath,
                        searchText: ''
                    };
                } else {
                    $scope.searchFont = {
                        selectedText: '',
                        previousSelectedText: '',
                        previousSelectedValue: '',
                        searchText: ''
                    };
                }
            }

            $scope.selectedTextProperties.id = $scope.textSelected.id;
            $scope.selectedTextProperties.skewX = $scope.textSelected.skewX;
            $scope.selectedTextProperties.skewY = $scope.textSelected.skewY;
            $scope.selectedTextProperties.currentFontSize = fabric.util.pxToPt($scope.textSelected.getFontSize());
            $scope.currentFontSizeValidator();
            $scope.selectedTextProperties.textAlign = $scope.textSelected.getTextAlign();
            $scope.selectedTextProperties.textVerticalAlign = $scope.textSelected.getTextVerticalAlign();
            $scope.selectedTextProperties.outlineWidth = $scope.textSelected.getOutlineWidth();
            $scope.selectedTextProperties.fontOption = $scope.textSelected.fontOption || 1;
            $scope.selectedTextProperties.fontColorOption = $scope.textSelected.fontColorOption || 1;
            $scope.selectedTextProperties.outlineColorOption = $scope.textSelected.outlineColorOption || 1;
            $scope.selectedTextProperties.Itext = $scope.textSelected.Itext;
            $scope.selectedTextProperties.editedSize = $scope.textSelected.editedSize;
            $scope.selectedTextProperties.tracking = $scope.textSelected.getTrackingAmount();
            $scope.selectedTextProperties.lineSpacing = $scope.textSelected.lineSpacing * 100;
            $scope.selectedTextProperties.locked = $scope.textSelected.locked;
            $scope.selectedTextProperties.defaultValue = $scope.textSelected.defaultValue;
            $scope.selectedTextProperties.prefix = $scope.textSelected.prefix;
            $scope.selectedTextProperties.suffix = $scope.textSelected.suffix;
            $scope.selectedTextProperties.referenceId = $scope.textSelected.referenceId;
            $scope.selectedTextProperties.texture = $scope.textSelected.texture;
            $scope.selectedTextProperties.textureScale = $scope.textSelected.textureScale || 100;
            const flip = $scope.textSelected.getFlip();
            $scope.selectedTextProperties.flipX = flip.flipX || false;
            $scope.selectedTextProperties.flipY = flip.flipY || false;
            $scope.selectedTextProperties.allowEdit = $scope.textSelected.allowEdit;
            $scope.selectedTextProperties.oldAllowEdit = $scope.textSelected.oldAllowEdit;
            $scope.selectedTextProperties.isTextPath = $scope.textSelected.isTextPath || false;
            $scope.selectedTextProperties.useAsMask = $scope.textSelected.useAsMask || false;
            $scope.selectedTextProperties.autofillSource = $scope.textSelected.autofillSource || '';
            $scope.selectedTextProperties.dateFormat = $scope.textSelected.dateFormat || {};
            $scope.selectedTextProperties.shadowType = $scope.textSelected.shadowType;
            $scope.selectedTextProperties.shadowOffset = $scope.textSelected.shadowOffset || 0;
            $scope.selectedTextProperties.shadowAngle = $scope.textSelected.shadowAngle || 0;
            $scope.selectedTextProperties.shadowBlur = $scope.textSelected.shadowBlur || 0;
        }

        function loadCircularTextData($scope) {
            if (!$scope.textSelected) {
                return;
            }
            // Load data
            $scope.selectedTextProperties.text = $scope.textSelected.text;
            $scope.selectedTextProperties.centerX = pixelsToMm($scope.textSelected.left, $scope.DPI);
            $scope.selectedTextProperties.centerY = pixelsToMm($scope.textSelected.top, $scope.DPI);
            $scope.selectedTextProperties.rotation = $scope.textSelected.angle;
            $scope.selectedTextProperties.verticalDiameter = pixelsToMm(
                $scope.textSelected.verticalDiameter,
                $scope.DPI
            );
            $scope.selectedTextProperties.horizontalDiameter = pixelsToMm(
                $scope.textSelected.horizontalDiameter,
                $scope.DPI
            );

            $scope.selectedTextProperties.startAngle = $scope.textSelected.startAngle;
            $scope.selectedTextProperties.endAngle = $scope.textSelected.endAngle;

            $scope.selectedTextProperties.caps = $scope.textSelected.caps;
            $scope.selectedTextProperties.fontPath = $scope.textSelected.fontPath;

            if ($scope.hasOwnProperty('searchFont')) {
                const selectedFont = fontManagerService.findFontByPath($scope.selectedTextProperties.fontPath);
                if (selectedFont) {
                    const fontName = `${selectedFont.Name} (${selectedFont.Style})`;
                    $scope.searchFont = {
                        selectedText: fontName,
                        previousSelectedText: fontName,
                        previousSelectedValue: $scope.selectedTextProperties.fontPath,
                        searchText: ''
                    };
                } else {
                    $scope.searchFont = {
                        selectedText: '',
                        previousSelectedText: '',
                        previousSelectedValue: '',
                        searchText: ''
                    };
                }
            }

            $scope.selectedTextProperties.id = $scope.textSelected.id;
            const fontSize = fabric.util.pxToPt($scope.textSelected.fontSize);
            $scope.selectedTextProperties.currentFontSize = fontSize;
            $scope.selectedTextProperties.fontSize = fontSize;
            $scope.selectedTextProperties.minSizePx =
                $scope.textSelected.minSizePx != undefined
                    ? fabric.util.pxToPt($scope.textSelected.minSizePx)
                    : fontSize;
            $scope.selectedTextProperties.maxSizePx =
                $scope.textSelected.maxSizePx != undefined
                    ? fabric.util.pxToPt($scope.textSelected.maxSizePx)
                    : fontSize;
            $scope.currentFontSizeValidator();
            $scope.selectedTextProperties.textAlign = $scope.textSelected.textAlign;
            $scope.selectedTextProperties.stroke = $scope.textSelected.stroke;
            $scope.selectedTextProperties.outlineColor = $scope.textSelected.stroke;
            $scope.selectedTextProperties.outlineWidth = $scope.textSelected.strokeWidth;
            $scope.selectedTextProperties.textSide = $scope.textSelected.convex ? 'convex' : 'concave';
            $scope.selectedTextProperties.tracking = $scope.textSelected.tracking;
            $scope.selectedTextProperties.fontOption = $scope.textSelected.fontOption || 1;
            $scope.selectedTextProperties.fontColorOption = $scope.textSelected.fontColorOption || 1;
            $scope.selectedTextProperties.outlineColorOption = $scope.textSelected.outlineColorOption || 1;
            $scope.selectedTextProperties.locked = $scope.textSelected.locked;
            $scope.selectedTextProperties.defaultValue = $scope.textSelected.defaultValue;
            $scope.selectedTextProperties.prefix = $scope.textSelected.prefix;
            $scope.selectedTextProperties.suffix = $scope.textSelected.suffix;
            $scope.selectedTextProperties.referenceId = $scope.textSelected.referenceId;
            const flip = $scope.textSelected.getFlip();
            $scope.selectedTextProperties.flipX = flip.flipX || false;
            $scope.selectedTextProperties.flipY = flip.flipY || false;
        }

        /**
         * @param {Object} $scope controller $scope.
         * @param {Object} canvas canvas where the text is placed.
         * @param {Object} color color object.
         * @param {boolean} save validates the persistent of the color change.
         */
        function textOutlineColor($scope, canvas, color, save = false) {
            $scope.textSelected.setOutlineColor(ElementsColorService.colorToWeb(color));
            if (save) {
                const colorObj = MultipleFontService.createColorObj({
                    alpha: color.alpha,
                    hex: '#' + color.HEX,
                    cmyk: color.cmyk.c + ',' + color.cmyk.m + ',' + color.cmyk.y + ',' + color.cmyk.k
                });
                $scope.textSelected.outlineColor = colorObj;
            }
            canvas.renderAll();
        }

        /**
         * @param {Object} $scope controller $scope.
         * @param {Object} canvas canvas where the text is placed.
         * @param {Object} colors colors objects.
         * @param {boolean} save validates the persistent of the color change.
         * @param {boolean} showInfoDialog show information dialog
         */
        function textFillColor($scope, canvas, colors, save = false, showInfoDialog = true) {
            $scope.textSelected.setColor(ElementsColorService.colorToWeb(colors));
            canvas.requestRenderAll();

            if (save) {
                MultipleFontService.saveFontColorToOption(
                    colors,
                    canvas.getActiveObject(),
                    $scope.editMode,
                    showInfoDialog
                );
            }
        }

        /**
         * @param {Object} $scope controller $scope.
         * @param {Object} canvas canvas where the text is placed.
         * @param {Object} colors colors objects.
         * @param {boolean} save validates the persistent of the color change.
         */
        function textCircularColorFill($scope, canvas, colors, save = false) {
            $scope.textSelected.set('fill', ElementsColorService.colorToWeb(colors));
            canvas.renderAll();
            if (save) {
                MultipleFontService.saveFontColorToOption(colors, canvas.getActiveObject(), $scope.editMode);
            }
        }

        /**
         * @param {Object} $scope controller $scope.
         * @param {Object} canvas canvas where the text is placed.
         * @param {Object} color color object.
         * @param {boolean} save validates the persistent of the color change.
         */
        function textCircularOutlineColor($scope, canvas, color, _save = false) {
            $scope.textSelected.set('opentypeStroke', ElementsColorService.colorToWeb(color));
            $scope.textSelected.set('stroke', ElementsColorService.colorToWeb(color));

            canvas.renderAll();
        }

        function addObjectWithTextMask(textUuid, targetObject) {
            objectsWithTextMasks[textUuid] = targetObject.uuid;
        }

        function removeObjectWithTextMask(textUuid) {
            delete objectsWithTextMasks[textUuid];
        }

        function applyTextMask(canvas, textMaskUuid = '', text = '') {
            return new Promise((resolve) => {
                let textObject = null;
                if (!textMaskUuid) {
                    textObject = canvas.getActiveObject();
                    textMaskUuid = textObject ? textObject.uuid : '';
                }

                if (!textMaskUuid) {
                    resolve({ applied: false });
                    return;
                }

                textObject = textObject || canvas._objects.find((o) => o.uuid === textMaskUuid && o.useAsMask);
                if (!textObject || !textObject.useAsMask) {
                    resolve({ applied: false });
                    return;
                }

                let targetObject;
                if (Object.keys(objectsWithTextMasks).length) {
                    targetObject = canvas._objects.find(
                        (o) => o.uuid === objectsWithTextMasks[textMaskUuid] && o.type === 'image-placeholder'
                    );
                }

                if (!targetObject) {
                    const objects = canvas._objects
                        .filter(
                            (o) => o.type === 'image-placeholder' && o.zIndex < textObject.zIndex && !o.textMaskUuid
                        )
                        .sort((a, b) => b.zIndex - a.zIndex);

                    if (objects.length) {
                        targetObject = objects[0];
                    }

                    if (!targetObject) {
                        resolve({ applied: false });
                        return;
                    }

                    targetObject.textMaskUuid = textObject.uuid;
                    targetObject.setEditedProp('textMaskUuid');
                    addObjectWithTextMask(textObject.uuid, targetObject);
                }

                textObject.set('text', textObject.text || text);

                if (textObject.text) {
                    const imageUrl = textObject.getTextAsMask();
                    fabric.Image.fromURL(imageUrl, function (clipPath) {
                        clipPath.top = textObject.top;
                        clipPath.left = textObject.left;
                        clipPath.originX = textObject.originX;
                        clipPath.originY = textObject.originY;
                        clipPath.scaleX = textObject.scaleX;
                        clipPath.scaleY = textObject.scaleY;
                        clipPath.absolutePositioned = true;
                        clipPath.angle = textObject.angle;
                        targetObject.item(0).set('clipPath', clipPath);
                        targetObject.textMaskUuid = textMaskUuid;
                        canvas.renderAll();
                        resolve({
                            applied: true,
                            targetObject: targetObject
                        });
                    });
                } else {
                    resolve({
                        applied: false,
                        targetObject: targetObject
                    });
                }
            });
        }

        async function removeTextMask(canvas, $scope, targetObject, textObject) {
            if (!textObject && targetObject) {
                textObject = canvas._objects.find((o) => o.uuid === targetObject.uuid);
            }
            if (textObject) {
                // restore object used as mask
                if ($scope.textAsMaskStoredObjects[textObject.uuid]) {
                    setTextMaskColor(canvas, $scope, textObject, $scope.textAsMaskStoredObjects[textObject.uuid]);
                    setTextMaskStroke(canvas, $scope, textObject, $scope.textAsMaskStoredObjects[textObject.uuid]);
                }
                delete $scope.textAsMaskStoredObjects[textObject.uuid];

                if (!targetObject) {
                    targetObject = canvas._objects.find(
                        (o) => o.type === 'image-placeholder' && o.uuid === textObject.uuid
                    );
                }
            }

            if (targetObject && targetObject.textMaskUuid) {
                if ($scope.editMode) {
                    targetObject.setEditedProp('textMaskUuid');
                }
                removeObjectWithTextMask(targetObject.textMaskUuid);
                targetObject.textMaskUuid = '';
                if (targetObject.item(0).clipPath) {
                    targetObject.item(0).set('clipPath', null);
                }
                canvas.renderAll();
            }

            return targetObject;
        }

        async function processTextMask(canvas, objects) {
            for (const object of objects) {
                const key = Object.keys(objectsWithTextMasks).find((k) => objectsWithTextMasks[k] === object.uuid);
                if (key) {
                    await applyTextMask(canvas, key);
                }
            }
        }

        function setTextMaskColor(canvas, $scope, object, copy) {
            if ($scope.editMode) {
                object.setEditedProp('fontColorsMap', 'color');
            }

            let fontColor;
            if (object.useAsMask) {
                fontColor = {
                    alpha: 0,
                    cmyk: {
                        c: 0,
                        m: 0,
                        y: 0,
                        k: 1
                    },
                    hex: '#000000',
                    HEX: '#000000'
                };
            } else {
                fontColor =
                    JSON.stringify(copy.fontColor).indexOf('NaN') > -1
                        ? 'rgb(0,0,0)'
                        : copy.fontColor === 'rgba(0,0,0,0)'
                        ? 'rgb(0,0,0)'
                        : copy.fontColor;
                fontColor = MultipleFontService.upgradeOldColor(fontColor);
            }

            object.fontColorsMap.set(1, fontColor);
            object.fontColorOption = 1;
            object.setColor(ElementsColorService.colorToWeb(fontColor));
            canvas.requestRenderAll();
        }

        function setTextMaskStroke(canvas, $scope, object, copy) {
            if ($scope.editMode) {
                object.setEditedProp('outlineColorsMap', 'outlineColor', 'outlineWidth');
            }

            let strokeColor, strokeWidth;
            if (object.useAsMask) {
                strokeColor = {
                    alpha: 1,
                    cmyk: {
                        c: 0.8095238095238095,
                        m: 0.6952380952380952,
                        y: 0,
                        k: 0.17647058823529416
                    },
                    hex: '#2840D2',
                    HEX: '#2840D2'
                };
                strokeWidth = 1;
            } else {
                strokeColor = copy.strokeColor.indexOf('NaN') > -1 ? 'rgb(0,0,0)' : copy.strokeColor;
                strokeColor = MultipleFontService.upgradeOldColor(strokeColor);
                strokeWidth = copy.strokeWidth || 0;
            }

            object.outlineColorsMap.set(1, strokeColor);
            object.outlineColorOption = 1;
            object.setOutlineColor(ElementsColorService.colorToWeb(strokeColor));
            object.setOutlineWidth(strokeWidth);
            canvas.requestRenderAll();
        }

        return {
            updateTexts: updateTexts,
            loadTextData: loadTextData,
            loadCircularTextData: loadCircularTextData,
            textCircularColorFill: textCircularColorFill,
            textFillColor: textFillColor,
            textOutlineColor: textOutlineColor,
            textCircularOutlineColor: textCircularOutlineColor,
            generateBoundedText: generateBoundedText,
            generatePathIText: generatePathIText,
            buildBoundedTextProperties: buildBoundedTextProperties,
            buildCircularTextProperties: buildCircularTextProperties,
            addObjectWithTextMask: addObjectWithTextMask,
            removeObjectWithTextMask: removeObjectWithTextMask,
            applyTextMask: applyTextMask,
            removeTextMask: removeTextMask,
            processTextMask: processTextMask,
            setTextMaskColor: setTextMaskColor,
            setTextMaskStroke: setTextMaskStroke
        };
    }
})();
